import { Link } from 'react-router-dom';
import { formatDownloads } from '@/utils/format';

export interface SoftwareItem {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
  version: string;
  size: string;
  platform: string;
  publishDate: string;
  downloads: number;
  rating: number;
  free: boolean;
  summary: string;
  tags: string[];
  cover: string;
}

interface SoftwareCardProps {
  software: SoftwareItem;
  variant?: 'grid' | 'list';
}

function RatingStars({ rating }: { rating: number }) {
  return (
    <span className="flex items-center gap-1 text-primary-600">
      <span className="w-4 h-4 flex items-center justify-center">
        <i className="ri-star-fill text-sm" />
      </span>
      <span className="text-xs font-medium text-foreground-700">{rating.toFixed(1)}</span>
    </span>
  );
}

export default function SoftwareCard({ software, variant = 'grid' }: SoftwareCardProps) {
  if (variant === 'list') {
    return (
      <div className="group rounded-lg border border-background-200 bg-background-50 p-4 flex gap-4 hover:border-primary-300 transition-colors">
        <Link to={`/detail/${software.id}`} className="shrink-0 cursor-pointer">
          <div className="w-28 h-28 sm:w-40 sm:h-28 rounded-md overflow-hidden bg-background-100">
            <img
              src={software.cover}
              alt={`${software.name} 软件封面`}
              title={`${software.name} 黄金投资软件`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </Link>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/detail/${software.id}`}
                  className="font-heading font-semibold text-base text-foreground-950 cursor-pointer hover:text-primary-700 transition-colors truncate"
                >
                  {software.name}
                </Link>
                {software.free && (
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[11px] font-medium whitespace-nowrap">
                    免费
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{software.summary}</p>
            </div>
          </div>

          <div className="mt-auto pt-3 flex items-end justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap text-xs text-foreground-500">
              <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600 whitespace-nowrap">
                {software.categoryName}
              </span>
              <span className="whitespace-nowrap">{software.version}</span>
              <span className="whitespace-nowrap">{software.size}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-download-2-line text-sm" />
                </span>
                {formatDownloads(software.downloads)}
              </span>
              <RatingStars rating={software.rating} />
            </div>
            <Link
              to={`/detail/${software.id}`}
              className="shrink-0 h-9 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-base" />
              </span>
              免费下载
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden flex flex-col hover:border-primary-300 transition-colors">
      <Link to={`/detail/${software.id}`} className="cursor-pointer">
        <div className="relative w-full h-40 bg-background-100 overflow-hidden">
          <img
            src={software.cover}
            alt={`${software.name} 软件封面`}
            title={`${software.name} 黄金投资软件`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {software.free && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-medium">
              免费下载
            </span>
          )}
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/detail/${software.id}`}
            className="font-heading font-semibold text-base text-foreground-950 cursor-pointer hover:text-primary-700 transition-colors truncate"
          >
            {software.name}
          </Link>
          <RatingStars rating={software.rating} />
        </div>
        <span className="mt-2 self-start px-2 py-0.5 rounded bg-background-100 text-foreground-600 text-xs">
          {software.categoryName}
        </span>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{software.summary}</p>

        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-foreground-500">
          <span className="whitespace-nowrap">{software.version}</span>
          <span className="whitespace-nowrap">{software.size}</span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-download-2-line text-sm" />
            </span>
            {formatDownloads(software.downloads)}
          </span>
        </div>

        <Link
          to={`/detail/${software.id}`}
          className="mt-4 h-9 rounded-md bg-primary-500 hover:bg-primary-600 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-1"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-download-line text-base" />
          </span>
          免费下载
        </Link>
      </div>
    </div>
  );
}